import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuestions } from './QuestionContext';
import { useGames } from './GameContext';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import Swal from 'sweetalert2';

const ContestContext = createContext();

const SOCKET_URL = process.env.REACT_APP_GAME_SOCKET_URL || 'http://localhost:3004/game'; // Game Server (isolated port)

export const ContestProvider = ({ children }) => {
  const { questions } = useQuestions();
  const { setActiveGameId } = useGames();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);

  const [gameState, setGameState] = useState('IDLE'); // IDLE, LOBBY, PLAYING, FINISHED
  const [roomCode, setRoomCode] = useState(null);
  const [isHost, setIsHost] = useState(false); // Sadece oyunu BEN oluşturduysam true
  const [players, setPlayers] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0); // Server'dan gelen oyunun soru sayısı
  const [hostQuestionData, setHostQuestionData] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timer, setTimer] = useState(0);
  // Sunucudan gelen timer referans noktaları (senkronizasyon için)
  const [timerServerStartTime, setTimerServerStartTime] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [revealedAnswer, setRevealedAnswer] = useState(null);
  const [liveStats, setLiveStats] = useState({ answered: 0, total: 0 });
  const [error, setError] = useState(null);
  const [lastAnswerResult, setLastAnswerResult] = useState(null);
  const [isProcessingEffect, setIsProcessingEffect] = useState(false);
  const [myPlayerData, setMyPlayerData] = useState(null);
  // Bu yarışmada bir oyuncunun kullanabileceği TOPLAM joker sayısı (0 = sınırsız)
  const [jokerLimit, setJokerLimit] = useState(0);
  // Oyuncunun bu yarışmada şu ana kadar kullandığı toplam joker
  const [jokersUsedInGame, setJokersUsedInGame] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [activeGames, setActiveGames] = useState([]);

  const rejoinAsHost = useCallback((pin, gameId) => {
    if (socketRef.current) {
        console.log('[ContestContext] Manually rejoining game as host:', pin);
        localStorage.setItem('activeRoomPin', pin);
        if (gameId) {
            localStorage.setItem('activeGameId', gameId);
            setActiveGameId(Number(gameId));
        }
        socketRef.current.emit('rejoin_host', { pin });
    }
  }, [setActiveGameId]);

  // Initialize Socket
  useEffect(() => {
    let newSocket;
    if (SOCKET_URL.includes('localhost')) {
      newSocket = io(SOCKET_URL);
    } else {
      // Production: SOCKET_URL is e.g. wss://workience.com/game-ws
      // We want to request: https://workience.com/game-ws/socket.io/
      // and join namespace /game
      try {
        const urlObj = new URL(SOCKET_URL);
        const host = `${urlObj.protocol}//${urlObj.host}`; // wss://workience.com
        const cleanPath = urlObj.pathname.endsWith('/') ? urlObj.pathname.slice(0, -1) : urlObj.pathname;
        newSocket = io(`${host}/game`, {
          path: `${cleanPath}/socket.io` // /game-ws/socket.io
        });
        console.log('[Socket] Initialized socket with host:', host, 'path:', `${cleanPath}/socket.io`);
      } catch (err) {
        console.error('[Socket] Error parsing SOCKET_URL, fallback to default:', err);
        newSocket = io(SOCKET_URL);
      }
    }
    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
        console.log('Connected to backend');
        newSocket.emit('get_active_games');
        const savedPin = localStorage.getItem('activeRoomPin');
        const playerPin = localStorage.getItem('player_pin');
        const playerNickname = localStorage.getItem('player_nickname');

        // Case 1: Moderator/Host Rejoin - savedPin varsa her zaman rejoin dene
        if (savedPin) {
            console.log('Attempting to rejoin game as host:', savedPin);
            newSocket.emit('rejoin_host', { pin: savedPin });
        }

        // Case 2: Player Rejoin
        const isPlayerPage = window.location.pathname.includes('/play');
        if (playerPin && playerNickname && isPlayerPage) {
            console.log('Attempting to automatic re-join as player:', playerNickname, 'into room', playerPin);
            newSocket.emit('join_game', { pin: playerPin, nickname: playerNickname });
        }
    });

    newSocket.on('active_games_list', (list) => {
        console.log('[ContestContext] Active games list received:', list);
        setActiveGames(list || []);
    });

    newSocket.on('game_restored', (data) => {
        console.log('[ContestContext] Game restored from server:', data);
        setRoomCode(data.pin);
        setIsHost(true); // Sunucu beni host olarak tanıdı → gerçekten bu odanın sahibiyim
        setGameState(data.status);
        setPlayers(data.players || []);
        if (data.currentQuestionIndex !== undefined) setCurrentQuestionIndex(data.currentQuestionIndex);
        if (data.totalQuestions) setTotalQuestions(data.totalQuestions);
        if (data.gameId) setActiveGameId(Number(data.gameId));

        // Oyun devam ediyorsa mevcut soruyu ve timer'ı restore et
        if (data.status === 'PLAYING' && data.currentQuestion) {
            setActiveQuestion(data.currentQuestion);
            const duration = data.currentQuestion.duration || 30;
            setTimerDuration(duration);
            const timeElapsed = data.currentQuestion.timeElapsed || 0;
            setTimerServerStartTime(Date.now() - timeElapsed * 1000);
            setTimer(Math.max(0, Math.ceil(duration - timeElapsed)));
            setIsTimerRunning(true);
        }
        // Liderlik tablosunu güncelle
        if (data.leaderboard && data.leaderboard.length > 0) {
            setPlayers(data.leaderboard);
        }

        // Auto-redirect host to the active page if they are not there already
        const currentPath = window.location.pathname;
        if (data.status === 'LOBBY') {
            if (currentPath !== '/lobby') {
                console.log('[ContestContext] Auto-redirecting host to /lobby');
                navigate('/lobby');
            }
        } else if (['PLAYING', 'BREAK', 'BREAK_TIME', 'BREAK_TIME_END'].includes(data.status)) {
            if (currentPath !== '/contest') {
                console.log('[ContestContext] Auto-redirecting host to /contest');
                navigate('/contest');
            }
        }
    });

    newSocket.on('game_created', (data) => {
        setRoomCode(data.pin);
        setJokerLimit(Number(data.jokerLimit) || 0);
        setJokersUsedInGame(0);
        setIsHost(true); // Oyunu ben oluşturdum → hostum
        localStorage.setItem('activeRoomPin', data.pin);
        setGameState('LOBBY');
        setPlayers([]);
    });

    newSocket.on('player_joined', (data) => {
        if (data.players) {
            setPlayers(data.players); 
        } else if (data.newPlayer) {
            setPlayers(prev => {
                if (prev.some(p => p.nickname === data.newPlayer.nickname)) return prev;
                return [...prev, data.newPlayer];
            });
        } else if (data.nickname) {
            setPlayers(prev => {
                if (prev.some(p => p.nickname === data.nickname)) return prev;
                return [...prev, { id: Date.now(), nickname: data.nickname, score: 0 }];
            });
        }
    });

    newSocket.on('player_left', (data) => {
        console.log('Player left event received:', data.nickname);
        setPlayers(prev => prev.filter(p => p.nickname !== data.nickname));
    });

    newSocket.on('joined_successfully', (data) => {
        console.log('Joined successfully', data);
        const sync = data?.sync;
        const incomingPin = data.pin || sync?.pin;

        // Joker tavanı + reconnect sonrası kullanılan joker sayısı
        const limit = data?.jokerLimit ?? sync?.jokerLimit;
        if (limit !== undefined && limit !== null) setJokerLimit(Number(limit) || 0);
        setJokersUsedInGame(Number(data?.data?.jokersUsedInGame) || 0);
        
        // 1. PIN ve Temel Kayıtlar
        if (incomingPin) {
            setRoomCode(incomingPin);
        } else {
            const savedPin = localStorage.getItem('player_pin');
            if (savedPin) setRoomCode(savedPin);
        }

        // 2. Durum Senkronizasyonu (Re-join/Restore)
        if (sync) {
            console.log('[ContestContext] Syncing state from server:', sync.status);
            setGameState(sync.status); 
            if (sync.currentQuestionIndex !== undefined) setCurrentQuestionIndex(sync.currentQuestionIndex);
            
            if (sync.question) {
                setActiveQuestion(sync.question);
                setTimer(sync.question.duration || 30);
                if (sync.question.serverStartTime || sync.question.timeElapsed !== undefined) {
                    const elapsedMs = (sync.question.timeElapsed || 0) * 1000;
                    setTimerServerStartTime(Date.now() - elapsedMs);
                    setTimerDuration(sync.question.duration || 30);
                    setIsTimerRunning(true);
                }
            }
            
            if (sync.lastAnswerResult) {
                setLastAnswerResult(sync.lastAnswerResult);
            }

            if (sync.countdown !== undefined && sync.countdown !== null) {
                setCountdown(sync.countdown);
            }

            if (sync.lateJoinMsg) {
                 Swal.fire({
                     title: 'Oyuna Dahil Oldunuz',
                     text: sync.lateJoinMsg,
                     icon: 'info',
                     confirmButtonText: 'Tamam',
                     confirmButtonColor: '#6366f1'
                 });
            }
        } else {
            setGameState('LOBBY');
        }

        // 3. Oyuncu Listesi Güncelleme
        setPlayers(prev => {
            if (data?.data) {
                setMyPlayerData(data.data);
                const exists = prev.some(p => p.id === data.data.id);
                return exists ? prev : [...prev, data.data];
            }
            return prev;
        });
    });

    newSocket.on('game_started', () => {
        setGameState('PLAYING');
        setCurrentQuestionIndex(0);
        setTimer(20);
        setIsTimerRunning(false);
        setShowAnswer(false);
        setRevealedAnswer(null);
        setLastAnswerResult(null);
        setCountdown(null);
    });

    newSocket.on('new_question', (q) => {
        console.log('New Question Received:', q);
        setGameState('PLAYING');
        setCurrentQuestionIndex(q.questionIndex);
        if (q.totalQuestions) setTotalQuestions(q.totalQuestions); // Oyunun gerçek soru sayısı
        setActiveQuestion(q);
        setLastAnswerResult(null);
        setShowAnswer(false);
        setRevealedAnswer(null);
        setHostQuestionData(null);
        
        // Süre: sunucu artık [new_question] içinde timing bilgisini gönderiyor
        const questionDuration = q.duration || q.totalTime || q.timeLimit || 30;
        setTimer(questionDuration);
        
        // Immediate Sync: Eğer pakette varsa bekleme yapma
        if (q.serverStartTime || q.timeElapsed !== undefined) {
            const elapsedMs = (q.timeElapsed || 0) * 1000;
            setTimerServerStartTime(Date.now() - elapsedMs);
            setTimerDuration(questionDuration);
            setIsTimerRunning(true);
            console.log('[ContestContext] Immediate timer sync from new_question');
        } else {
            setIsTimerRunning(false);
        }

        setLiveStats({ answered: 0, total: 0 });
        setCountdown(null);
    });

    // Host-specific data update (answers)
    newSocket.on('host_question_data', (fullData) => {
        console.log('Host Data Received (Answers):', fullData);
        setHostQuestionData(fullData);
    });

    // timer_started: moderatör panelinin sayacını da senkronize et ve timer referanslarını kaydet
    newSocket.on('timer_started', ({ serverStartTime, duration, timeElapsed } = {}) => {
        console.log('[ContestContext] timer_started sync:', { serverStartTime, duration, timeElapsed });
        if (duration) {
            const elapsedMs = (timeElapsed || 0) * 1000;
            setTimerServerStartTime(Date.now() - elapsedMs);
            setTimerDuration(duration);

            const remaining = Math.max(0, duration - (timeElapsed || 0));
            setTimer(Math.ceil(remaining));
            console.log(`[ContestContext] sync stats: elapsed=${(timeElapsed || 0).toFixed(2)}s, remaining=${remaining.toFixed(2)}s`);
        }
        setIsTimerRunning(true);
    });

    // *** CRITICAL: Sunucudan doğru cevap geldiğinde ***
    newSocket.on('answer_revealed', (data) => {
        console.log('[ContestContext] answer_revealed received:', data);
        setShowAnswer(true);
        setRevealedAnswer(data); // correctAnswer, correctAnswers, options içeriyor
        setIsTimerRunning(false);
    });

    // *** CRITICAL: Her oyuncunun kişisel sonucu ***
    newSocket.on('question_result', (result) => {
        console.log('[ContestContext] question_result received:', result);
        setLastAnswerResult(result);
        // Puan tablosunu güncelle
        setPlayers(prev => prev.map(p => {
            if (p.id === newSocket.id) {
                return { ...p, score: result.totalScore };
            }
            return p;
        }));
    });

    newSocket.on('answer_result', (result) => {
        console.log('Answer Result (legacy):', result);
        setLastAnswerResult(result);
    });

    newSocket.on('live_stats_update', (data) => {
        console.log('Live stats:', data);
        setLiveStats(data);
    });



    // Sunucu timer_stopped gönderdiğinde timer'ı dondur
    newSocket.on('timer_stopped', () => {
        console.log('[ContestContext] timer_stopped received');
        setIsTimerRunning(false);
    });

    // *** YENİ EKLENEN KISIM: Ara liderlik tablosu moderatör için ***
    newSocket.on('mid_game_leaderboard', (leaderboard) => {
        console.log('[ContestContext] mid_game_leaderboard received:', leaderboard);
        console.log('[ContestContext] mid_game_leaderboard received:', leaderboard);
        setPlayers(leaderboard);
        setCountdown(null); 
    });

    // Oyun bitti — sadece burada FINISHED state'e geç
    newSocket.on('game_over', (data) => {
        console.log('[ContestContext] game_over received:', data);
        setGameState('FINISHED');
        setCountdown(null);
        if (data.leaderboard) {
            setPlayers(data.leaderboard);
        }
        localStorage.removeItem('activeRoomPin');
        setRoomCode(null); // Clear room code so the green banner disappears
        setIsHost(false);
    });

    newSocket.on('break_time', (data) => {
        console.log('[ContestContext] break_time received:', data);
        setGameState('BREAK_TIME');
        setTimer(data.duration || 180);
        setTimerDuration(data.duration || 180);
        setTimerServerStartTime(Date.now());
        setIsTimerRunning(true);
    });

    newSocket.on('error', (err) => {
        console.error('Socket error:', err);
        setError(err.message || 'Unknown socket error');
        
        // Eğer sunucu yeniden başlatılmışsa ve eski PIN kalmışsa, kullanıcıyı ana ekrana at
        if (err.message === 'Oyun bulunamadı veya sona ermiş.' || err.message === 'Oyun bulunamadı.') {
            setCountdown(null);
            localStorage.removeItem('activeRoomPin');
            localStorage.removeItem('activeGameId');
            setActiveGameId(null);
            setRoomCode(null);
            setIsHost(false);
            setGameState('IDLE');
            if (window.location.pathname !== '/') {
                navigate('/');
            }
        }
    });

    newSocket.on('join_error', (data) => {
        console.error('Join error received:', data);
        Swal.fire({
            icon: 'error',
            title: 'Katılım Hatası',
            text: data.message || 'Oyuna katılamadınız.',
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Tamam'
        }).then(() => {
            // Eğer re-join sırasında hata aldıysa ve zaten /play sayfasındaysa, ana sayfaya yönlendir
            if (window.location.pathname.includes('/play')) {
                localStorage.removeItem('player_pin');
                window.location.href = '/join';
            }
        });
    });

    newSocket.on('question_countdown', (count) => {
        console.log(`[SOCKET] question_countdown received: ${count} at ${new Date().toLocaleTimeString()}`);
        setCountdown(count);
        // Ensure UI transitions to COUNTDOWN state if not already there
        if (count > 0) {
            setGameState('COUNTDOWN');
        } else {
            // When count is 0, we might want to stay in COUNTDOWN for a split second 
            // until new_question arrives, but for now just clear it or let new_question handle it
            // setTimeout(() => setCountdown(null), 500);
        }
    });

    newSocket.on('auto_next_question', ({ pin }) => {
        console.log('[ContestContext] auto_next_question triggered for PIN:', pin);
        // We trigger nextQuestion() which will call the server's next_question
        // This is only relevant for the moderator/host
        setCountdown(null);
        // We use a small delay to ensure UI is ready
        setTimeout(() => {
            // Internal call to avoid circular dependency
            const currentSocket = socketRef.current;
            const currentRoomCode = localStorage.getItem('activeRoomPin');
            if (currentSocket && currentRoomCode) {
                console.log('[ContestContext] Auto-triggering next_question...');
                currentSocket.emit('next_question', { pin: currentRoomCode });
            }
        }, 100);
    });

    newSocket.on('no_players_left', (data) => {
        console.log('[ContestContext] no_players_left received:', data);
        Swal.fire({
            title: 'Yarışma Bitti!',
            text: data.message || 'Aktif yarışmacı kalmadı, yarışma bitiriliyor.',
            icon: 'warning',
            confirmButtonText: 'Tamam',
            confirmButtonColor: '#6366f1'
        }).then(() => {
            resetContest();
            window.location.href = '/'; 
        });
    });

    newSocket.on('game_terminated', (data) => {
        console.log('[ContestContext] game_terminated received:', data);
        Swal.fire({
            title: 'Yarışma Sonlandırıldı',
            text: data.message || 'Yarışma moderatör tarafından sonlandırıldı veya bir hata oluştu.',
            icon: 'info',
            iconColor: '#6366f1',
            confirmButtonText: 'Tamam',
            confirmButtonColor: '#6366f1',
            background: '#ffffff',
            customClass: {
                title: 'fw-bold text-dark',
                popup: 'rounded-4 shadow-lg'
            }
        }).then(() => {
            resetContest();
            window.location.href = '/';
        });
    });

    return () => newSocket.close();
  }, []); // Empty dependency array to run once on mount


  // Timer Logic — sunucu startTime'a göre senkronize countdown
  useEffect(() => {
    if (!isTimerRunning || !timerServerStartTime || timerDuration <= 0) return;

    const interval = setInterval(() => {
      const elapsed = (Date.now() - timerServerStartTime) / 1000;
      const remaining = Math.max(0, timerDuration - elapsed);
      const remainingCeil = Math.ceil(remaining);
      setTimer(remainingCeil);

      if (remaining <= 0) {
        setIsTimerRunning(false);
        clearInterval(interval);
        // SADECE gerçek host show_answer gönderir. Eskiden roomCode'u olan
        // HER istemci (oyuncular dahil) süre bitince aynı anda emit ediyordu —
        // 500 kişilik senkron istek fırtınası. Sunucu tarafında ayrıca süre
        // dolunca otomatik reveal var; bu emit sadece erken tetikleyicidir.
        if (isHost && socket && roomCode) {
          socket.emit('show_answer', { pin: roomCode });
        }
      }
    }, 250); // Her 250ms'de bir güncelle — ekranın her ikisi de aynı değeri gösterir

    return () => clearInterval(interval);
  }, [isTimerRunning, timerServerStartTime, timerDuration, socket, roomCode, isHost]);

  const createRoom = useCallback((gameTemplate) => {
    if (roomCode) {
      console.warn('[ContestContext] Room already exists:', roomCode);
      return;
    }
    if (socket) {
        const base = gameTemplate || { title: 'New Game' };
        // Yarışmayı BAŞLATAN (giriş yapmış) kullanıcının gerçek id'sini gönder.
        // Admin tespiti şablonun sahibine göre değil, başlatana göre yapılsın;
        // böylece admin başlattığında ana ekrana düşer.
        const payload = (user && user.id_users != null)
            ? { ...base, host_id: user.id_users }
            : base;
        socket.emit('create_game', { gameTemplate: payload });
    }
  }, [roomCode, socket, user]);

  const joinGame = useCallback((pin, nickname) => {
      if (socket) {
          socket.emit('join_game', { pin, nickname });
      }
  }, [socket]);

  const startGame = useCallback(() => {
    if (socket && roomCode) {
        socket.emit('start_game', { pin: roomCode });
    }
  }, [socket, roomCode]);

  const resetContest = useCallback(() => {
    setGameState('IDLE');
    setRoomCode(null);
    setIsHost(false);
    setPlayers([]);
    setCurrentQuestionIndex(0);
    setTotalQuestions(0);
    setHostQuestionData(null);
    setActiveQuestion(null);
    setIsTimerRunning(false);
    setTimer(0);
    setTimerServerStartTime(null);
    setTimerDuration(0);
    setShowAnswer(false);
    setRevealedAnswer(null);
    setLiveStats({ answered: 0, total: 0 });
    setLastAnswerResult(null);
    setCountdown(null);
    localStorage.removeItem('activeRoomPin');
    localStorage.removeItem('activeGameId');
    setActiveGameId(null);
  }, [setActiveGameId]);

  // Sıradaki soruya geç — ARTIK liderlik tablosu açmıyor, direkt sonraki soru
  const nextQuestion = useCallback(() => {
    if (isProcessingEffect) return;
    setIsProcessingEffect(true);
    setTimeout(() => setIsProcessingEffect(false), 2000);

    // Cevabı sıfırla ve sunucuya next_question gönder
    setShowAnswer(false);
    setRevealedAnswer(null);
    setLastAnswerResult(null);

    // Sunucu next_question emit edecek, yeni soru geldiğinde 'new_question' event'i tetiklenecek
    // Eğer oyun bittiyse sunucu 'game_over' emit edecek (LEADERBOARD oradan açılacak)
    if (socket && roomCode) {
        console.log('Emitting next_question...');
        socket.emit('next_question', { pin: roomCode });
    }
  }, [isProcessingEffect, socket, roomCode]);

  const toggleTimer = useCallback(() => {
    const newTimerState = !isTimerRunning;
    setIsTimerRunning(newTimerState);
    
    if (newTimerState && socket && roomCode) {
        socket.emit('start_timer', { pin: roomCode });
    }
  }, [isTimerRunning, socket, roomCode]);

  const addTime = useCallback((seconds) => setTimer(prev => Math.max(0, prev + seconds)), []);

  // *** CRITICAL FIX: revealAnswer artık show_answer emit ediyor ***
  const revealAnswer = useCallback(() => {
    if (!socket || !roomCode) return;
    // Sunucuya show_answer gönder — sunucu answer_revealed broadcast edecek
    socket.emit('show_answer', { pin: roomCode });
    // Local state: timer'ı durdur ama sıfırlama (sunucudan answer_revealed gelince showAnswer set edilecek)
    setIsTimerRunning(false);
  }, [socket, roomCode]);

  // Liderlik tablosunu göster (sadece moderatöre, oyun bitmemişse ara sıralama)
  const showLeaderboard = useCallback(() => {
    if (socket && roomCode) {
        socket.emit('show_leaderboard', { pin: roomCode });
    }
  }, [socket, roomCode]);

  const terminateGame = useCallback(() => {
    if (socket && roomCode) {
        console.log('[ContestContext] Terminating game early with PIN:', roomCode);
        socket.emit('terminate_game', { pin: roomCode });
        resetContest(); // Locally reset context immediately
    } else {
        console.warn('[ContestContext] Cannot terminate game: socket or roomCode missing', { socket: !!socket, roomCode });
        resetContest(); // Still reset locally to be safe if UI stuck
    }
  }, [socket, roomCode, resetContest]);

  const kickUser = useCallback((socketId) => {
    if (socket && roomCode) {
        const player = players.find(p => p.id === socketId);
        const nickname = player ? player.nickname : 'Bu oyuncuyu';

        Swal.fire({
            title: 'Emin misiniz?',
            text: `${nickname} isimli oyuncuyu odadan atmak istediğinize emin misiniz?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Evet, At',
            cancelButtonText: 'Vazgeç'
        }).then((result) => {
            if (result.isConfirmed) {
                console.log('[ContestContext] Kicking user:', socketId);
                socket.emit('kick_user', { pin: roomCode, socketIdToKick: socketId });
                
                // Show a brief loading toast instead of "Success" immediately
                const Toast = Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                Toast.fire({
                    icon: 'info',
                    title: 'İşlem yapılıyor...'
                });
            }
        });
    }
  }, [socket, roomCode, players]);


  // Derive merged question (public data + host answers + reveal data)
  const mergedQuestion = (() => {
    let base = activeQuestion || (questions && questions[currentQuestionIndex]);
    if (hostQuestionData && base && base.questionIndex === hostQuestionData.questionIndex) {
        base = { ...base, ...hostQuestionData };
    }
    if (revealedAnswer && base) {
        // Revealed options (is_correct dahil) merge et
        base = { ...base, ...revealedAnswer };
    }
    return base;
  })();

  return (
    <ContestContext.Provider value={{
      gameState,
      roomCode,
      isHost,
      players,
      currentQuestion: mergedQuestion,
      currentQuestionIndex,
      totalQuestions,        // Server'dan gelen oyunun gerçek soru sayısı
      timer,
      isTimerRunning,
      showAnswer,
      revealedAnswer,
      error,
      createRoom,
      joinGame,
      startGame,
      nextQuestion,
      showLeaderboard,
      toggleTimer,
      addTime,
      revealAnswer,
      lastAnswerResult,
      liveStats,
      socket,
      resetContest,
      terminateGame,
      kickUser,
      myPlayerData,
      jokerLimit,
      jokersUsedInGame,
      setJokersUsedInGame,
      countdown,
      activeGames,
      rejoinAsHost
    }}>
      {children}
    </ContestContext.Provider>
  );
};

export const useContest = () => useContext(ContestContext);
